import { ref } from 'vue'
import type { ChartData } from '~/models/statistics'
import type { ConfigAppType } from '~/types/app.type'

export function useArticleContent() {
  const $config: ConfigAppType = useRuntimeConfig()
  const { $restClient } = useNuxtApp()
  const { getHubspotFormIds } = useHubSpotForm()

  const chartIds = ref<string[]>([])
  const chartsData = ref<ChartData[]>([])
  const formidableFormIds = ref<string[]>([])
  const hubspotFormsIds = ref<string[]>([])

  const positionQuote = (content?: HTMLElement, quote?: HTMLElement) => {
    if (!content || !quote) return

    const paragraphs = content?.querySelectorAll('p') || []

    if (paragraphs.length > 1) {
      const middleParagraphIndex = Math.floor(paragraphs.length / 2)
      paragraphs[middleParagraphIndex].insertAdjacentElement('afterend', quote)
    }
  }

  const insertGraphs = async (content?: HTMLElement) => {
    if (!content) return

    const placeholders =
      content?.querySelectorAll('.visualizer-front-container') || []
    placeholders.forEach((placeholder: HTMLElement) => {
      const match = placeholder
        ?.querySelector('.visualizer-front')
        ?.getAttribute('class')
      const matchArray = /visualizer-front-(\d+)/.exec(match)
      if (matchArray?.length) {
        const [, id] = matchArray
        chartIds.value.push(id)
      }
    })

    if (chartIds.value.length) {
      chartsData.value = (
        await $restClient.getGraphsData({
          ids: chartIds.value,
        })
      ).map((chart) => {
        chart.key = 0
        return chart
      })
    }

    // Remove the placeholders from the DOM since they'll be replaced by the ArticleCharts component
    await nextTick(() =>
      placeholders.forEach((placeholder: HTMLElement) => {
        if (!placeholder) return
        placeholder.remove()
      }),
    )
  }

  const insertFormidableForms = async (content?: HTMLElement) => {
    if (!content) return

    const rawForms = content?.querySelectorAll('.frm_forms') || []
    const formIds: string[] = []
    rawForms.forEach((rawForm: HTMLElement) => {
      let formId = (rawForm.querySelector('[name=form_id]') as HTMLInputElement)
        ?.value
      if (!formId) return

      if (formId === '9') formId = $config.public.formidableForms.contactUs

      formIds.push(formId)
    })
    formidableFormIds.value = formIds

    // Remove the raw forms from the DOM since they'll be replaced by the ArticleForms component
    if (formIds.length) {
      await nextTick(() =>
        rawForms.forEach((rawForm: HTMLElement) => {
          if (!rawForm) return
          rawForm.remove()
        }),
      )
    }
  }

  const insertHubSpotForms = async (content?: HTMLElement) => {
    if (!content) return
    hubspotFormsIds.value = getHubspotFormIds(content)
  }

  const setupContent = async (content: HTMLElement, quote: HTMLElement) => {
    positionQuote(content, quote)
    await insertGraphs(content)
    await insertFormidableForms(content)
    await insertHubSpotForms(content)
  }

  return {
    chartIds,
    chartsData,
    formidableFormIds,
    hubspotFormsIds,
    positionQuote,
    insertGraphs,
    insertFormidableForms,
    insertHubSpotForms,
    setupContent,
  }
}
